import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  private refreshInFlight$: Observable<boolean> | null = null;

  constructor(
    private _httpClient: HttpClient,
    private jwtHelper: JwtHelperService
  ) {}

  get accessToken(): string | null {
    return localStorage.getItem('jwt');
  }

  get refreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  setTokens(accessToken: string, refreshToken?: string | null): void {
    if (accessToken) {
      localStorage.setItem('jwt', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  clearTokens(): void {
    localStorage.removeItem('jwt');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('jwt_last_refresh');
    sessionStorage.removeItem('current_user');
  }

  hasValidAccessToken(): boolean {
    const token = this.accessToken;
    return !!(token && !this.jwtHelper.isTokenExpired(token));
  }

  hasValidRefreshToken(): boolean {
    const token = this.refreshToken;
    return !!(token && !this.jwtHelper.isTokenExpired(token));
  }

  /**
   * Refresh access token using stored refresh token.
   * Concurrent callers share one in-flight request.
   * Pass force=true to rotate tokens while access is still valid (sliding 30-day window).
   * Only clears tokens when the server rejects the refresh token — not on network/transient errors.
   */
  refreshAccessToken(force: boolean = false): Observable<boolean> {
    if (!force && this.hasValidAccessToken()) {
      return of(true);
    }

    if (!this.hasValidRefreshToken()) {
      return of(false);
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this._httpClient.post<any>(
      'api/auth/refresh',
      { refresh_token: this.refreshToken },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      }
    ).pipe(
      map((response) => {
        if (response?.status && response?.token) {
          this.setTokens(response.token, response.refresh_token);
          localStorage.setItem('jwt_last_refresh', String(Date.now()));
          return true;
        }
        // Server responded but rejected the refresh token
        this.clearTokens();
        return false;
      }),
      catchError((error: unknown) => {
        if (this.isInvalidRefreshError(error)) {
          this.clearTokens();
        }
        return of(false);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }
  ensureValidAccessToken(): Observable<boolean> {

    if (this.hasValidAccessToken()) {
      return of(true);
    }

    if (this.hasValidRefreshToken()) {
      return this.refreshAccessToken(true);
    }

    return of(false);
  }
  //ensureValidAccessToken(): Observable<boolean> {
  //  if (this.hasValidAccessToken()) {
  //    return of(true);
  //  }
  //  return this.refreshAccessToken();
  //}

  private isInvalidRefreshError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    // 401/403 = refresh token invalid/expired; do not wipe on network/5xx
    return error.status === 401 || error.status === 403;
  }
}
